import { ElementRef, Injectable } from '@angular/core';
import { UserPreferencesUnitSettingsService } from 'app/shared/services/user-preferences-unit-settings/user-preferences-unit-settings.service';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import moment from 'moment';

@Injectable()
export class HelperService {

  public ONE_SECOND = 1000;
  public ONE_MINUTE = 60 * this.ONE_SECOND;
  public TEN_MINUTES = 10 * this.ONE_MINUTE;
  public ONE_HOUR = 60 * this.ONE_MINUTE;
  public THREE_HOURS = 3 * this.ONE_HOUR;
  public SIX_HOURS = 6 * this.ONE_HOUR;
  public HALF_DAY = 12 * this.ONE_HOUR;
  public ONE_DAY = 24 * this.ONE_HOUR;

  constructor(private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private userPreferencesUnitSettingsService: UserPreferencesUnitSettingsService) { }

  public generateHistoryMissingEntries<T extends { timestamp: number }>(data: T[], rangeHours: number, stepMinutes: number, defaultValue: Partial<T>): T[] {
    const stepMillis = stepMinutes * this.ONE_MINUTE;
    const count = Math.ceil(rangeHours * 60 / stepMinutes);

    const from = (Math.floor(new Date().getTime() / stepMillis) - count) * stepMillis;

    return this.generateEntries(data, from, count, stepMillis, defaultValue);
  }

  public generateMissingEntries<T extends { timestamp: number }>(data: T[], rangeHours: number, stepMinutes: number, defaultValue: Partial<T>): T[] {
    const stepMillis = stepMinutes * this.ONE_MINUTE;
    const count = Math.ceil(rangeHours * 60 / stepMinutes);

    const from = Math.floor(new Date().getTime() / stepMillis) * stepMillis;

    return this.generateEntries(data, from, count, stepMillis, defaultValue);
  }

  private generateEntries<T extends { timestamp: number }>(data: T[], from: number, count: number, stepMillis: number, defaultValue: Partial<T>): T[] {
    const entries = Array.from(Array(count), (v, k) => k + 1)
      .map(k => from + k * stepMillis)
      .map(dateTime => {
        const entry = data.find(d => d.timestamp === dateTime);
        return entry ? entry : Object.assign({}, defaultValue, { timestamp: dateTime }) as T;
      });

    return entries;
  }

  public calcStepsAxis(maxValue) {
    if (maxValue % 5 === 0) {
      maxValue = Math.ceil(maxValue / 4) * 5;
    }
    const dividers = [0.5, 1, 1.5, 2, 5, 10, 15, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 4000];
    let dividerIdx = 0;
    while (maxValue / dividers[dividerIdx] > 7) {
      dividerIdx++;
    }
    return {
      stepSize: dividers[dividerIdx],
      yAxisMax: Math.ceil(maxValue / dividers[dividerIdx]) * dividers[dividerIdx]
    };
  }

  public generateChartLabels(timestamps: number[], rangeHours: number, displayNow: boolean, showLast = true, displayZeroFormat?: string) {
    return timestamps.map((t, i) => this.getLabelValue(t, rangeHours, i, timestamps.length, displayNow, showLast, displayZeroFormat));
  }

  public generateChartDateLabels(startTime: number, endTime: number, chartRef: ElementRef, dataLength: number) {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const timeDiff = Math.abs(startDate.getTime() - endDate.getTime());
    const resolution = timeDiff / chartRef.nativeElement.offsetWidth;
    const mResolution = resolution / 60000; // That would be in minutes
    const hResolution = mResolution / 60;  // Minutes are not enough on very Low Res for long periods
    const pDistance = (this.userPreferencesUnitSettingsService.getTimeFormat().slice(0, 2) === 'hh' && mResolution < 15) ? 70 : 48;
    const distance = pDistance * mResolution;
    let interval;
    distance < 60 ? interval = 1 :
        distance < 120 ? interval = 2 :
            distance < 180 ? interval = 3 :
                distance < 240 ? interval = 4 :
                    distance < 360 ? interval = 6 :
                        distance < 720 ? interval = 12 :
                            interval = 24;

    let previous = -pDistance;
    const divider = dataLength / chartRef.nativeElement.offsetWidth;
    const dates = new Array(dataLength >= 0 ? dataLength : 0).fill('');
    for (let x = 0; x < chartRef.nativeElement.offsetWidth; x++) {
      const pixelTime = startDate.getTime() + Math.trunc(x * resolution);
      const pixelTimeHour = new Date(pixelTime).getMinutes();
      const si2 = !((new Date(pixelTime).getHours()) % interval) && (interval === 2);
      const si3 = !((new Date(pixelTime).getHours()) % interval) && (interval === 3);
      const si4 = !((new Date(pixelTime).getHours()) % interval) && (interval === 4);
      const si6 = !((new Date(pixelTime).getHours()) % interval) && (interval === 6);
      const si12 = !((new Date(pixelTime).getHours()) % interval) && (interval === 12);
      const pixelTime24Hour = (new Date(pixelTime).getHours());
      const atp = (pixelTimeHour < (mResolution) || (60 - pixelTimeHour) < (mResolution));
      if (x - previous >= pDistance) {
        if (atp && (pixelTime24Hour < hResolution)) {
          previous = x;
          dates[Math.trunc(x * divider)] = this.dateTimeFormatPreferencePipe.transform(moment(pixelTime).toDate(), 'dm');
        } else if (atp && ((interval === 1)) || si2 || si3 || si4 || si6 || si12) {
          previous = x;
          dates[Math.trunc(x * divider)] = this.dateTimeFormatPreferencePipe.transform(new Date(0, 0, 0, new Date(pixelTime + 900000).getHours()), 't');
        }
      }
    }

    return dates;
  }

  private getLabelValue(timestamp: number, rangeHours: number, index: number, count: number, displayNow: boolean, showLast: boolean, displayZeroFormat?: string) {
    const date = new Date(timestamp);

    switch (this.getLabelType(date, rangeHours, index, count, displayNow, showLast)) {
      case LabelType.TextAndLine:
        return this.formatLabel(date, displayZeroFormat);
      case LabelType.LineOnly:
        return '';
    }
    return null;
  }

  private formatLabel(date: Date, displayZeroFormat?: string) {
    if (date) {
      if (date.getHours() === 0 && date.getMinutes() === 0) {
        if (displayZeroFormat) {
          return moment(date).format(displayZeroFormat);
        } else {
          return date.getDate() + '. ' + date.toLocaleString('en-us', {month: 'short'});
        }
      }
      return this.dateTimeFormatPreferencePipe.transform(date, 't');
    }
    return null;
  }

  private getLabelType(date: Date, rangeHours: number, index: number, count: number, displayNow: boolean, showLast: boolean): LabelType {
    if (!date) { return LabelType.None; }

    const isLast = index === count - 1;

    if (displayNow && isLast && showLast) {
      return LabelType.TextAndLine;
    }

    const minutes = date.getMinutes();
    const hours = date.getHours();

    if (rangeHours >= 120) {
      if ((minutes === 0) && (hours % 12  === 0)) {
        if (displayNow && index >= count - 30 && showLast) { return LabelType.LineOnly; }
        return LabelType.TextAndLine;
      }
    } else if (rangeHours >= 72) {
      if ((minutes === 0) && (hours % 6 === 0)) {
        if (displayNow && index >= count - 16 && showLast) { return LabelType.LineOnly; }
        return LabelType.TextAndLine;
      }
    } else if (rangeHours >= 48) {
      if ((minutes === 0) && (hours % 4  === 0)) {
        if (displayNow && index >= count - 9 && showLast) { return LabelType.LineOnly; }
        return LabelType.TextAndLine;
      }
    } else if (rangeHours >= 12) {
      if ((minutes === 0) && (hours % 2 === 0)) {
        if (displayNow && index >= count - 6 && showLast) { return LabelType.LineOnly; }
        return LabelType.TextAndLine;
      }
    } else {
      if (minutes % 30 === 0) {
        if (displayNow && index >= count - 3 && showLast) { return LabelType.LineOnly; }
        return LabelType.TextAndLine;
      }
    }

    return LabelType.None;
  }

  public format(s: string, args: {}) {
    let str = s;

    Object.keys(args).forEach(a => str = str.replace(new RegExp(`\\{${a}\\}`, 'gi'), args[a]));

    return str;
  }
}

enum LabelType { TextAndLine, LineOnly, None }
